import { useState } from 'react';
import { ChevronRight, ChevronDown, Network } from 'lucide-react';
import type { ServiceInfo } from '@/types';
import { useClusterServices, useClusterNamespaces, useServiceEndpoints } from './useClusterData';

interface Props {
  siteId: string;
}

function ServiceRow({ siteId, service }: { siteId: string; service: ServiceInfo }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className={`border-b border-surface-elevated hover:bg-surface-elevated/30 cursor-pointer transition-colors ${expanded ? 'bg-surface-elevated/20' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">
          <div className="flex items-center gap-1.5">
            {expanded ? <ChevronDown size={12} className="text-gold" /> : <ChevronRight size={12} className="text-silver-muted" />}
            {service.name}
          </div>
        </td>
        <td className="px-4 py-2.5 text-xs text-silver-muted">{service.namespace}</td>
        <td className="px-4 py-2.5">
          <span className="text-xs px-2 py-0.5 rounded bg-surface-elevated text-silver-bright">{service.type}</span>
        </td>
        <td className="px-4 py-2.5 text-xs text-silver-muted font-mono">{service.clusterIP}</td>
        <td className="px-4 py-2.5 text-xs text-silver-muted font-mono">
          {service.ports.map((p) => `${p.port}/${p.protocol}`).join(', ')}
        </td>
        <td className="px-4 py-2.5 text-xs font-mono">
          <span className={service.endpointsReady > 0 ? 'text-status-healthy' : 'text-status-warning'}>
            {service.endpointsReady}
          </span>
          {service.endpointsNotReady > 0 && (
            <span className="text-status-warning ml-1">({service.endpointsNotReady} not ready)</span>
          )}
        </td>
        <td className="px-4 py-2.5 text-xs text-silver-muted">{service.age}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-surface-deep/50 px-6 py-3">
            <EndpointDetail siteId={siteId} namespace={service.namespace} serviceName={service.name} service={service} />
          </td>
        </tr>
      )}
    </>
  );
}

function EndpointDetail({ siteId, namespace, serviceName, service }: {
  siteId: string; namespace: string; serviceName: string; service: ServiceInfo;
}) {
  const { data: endpoints, isLoading } = useServiceEndpoints(siteId, namespace, serviceName);

  return (
    <div className="space-y-2">
      {/* Selector */}
      {Object.keys(service.selector).length > 0 && (
        <div className="text-xs">
          <span className="text-silver-muted">Selector: </span>
          {Object.entries(service.selector).map(([k, v]) => (
            <span key={k} className="font-mono text-silver-bright mr-2">{k}={v}</span>
          ))}
        </div>
      )}

      {/* Ports detail */}
      <div className="text-xs">
        <span className="text-silver-muted">Ports: </span>
        {service.ports.map((p, i) => (
          <span key={i} className="font-mono text-silver-bright mr-3">
            {p.name ? `${p.name}:` : ''}{p.port}→{p.targetPort}/{p.protocol}
            {p.nodePort ? ` (NodePort: ${p.nodePort})` : ''}
          </span>
        ))}
      </div>

      {/* Endpoints */}
      {isLoading ? (
        <div className="text-xs text-silver-muted animate-pulse">Loading endpoints...</div>
      ) : endpoints && endpoints.subsets.length > 0 ? (
        <div className="text-xs">
          <h5 className="text-silver-muted uppercase tracking-wider text-[10px] mb-1">Endpoints</h5>
          <div className="space-y-0.5">
            {endpoints.subsets.flatMap((s) => [
              ...s.addresses.map((a, i) => (
                <div key={`r-${i}`} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-status-healthy" />
                  <span className="font-mono text-silver-bright">{a.ip}</span>
                  {a.nodeName && <span className="text-silver-muted">({a.nodeName})</span>}
                </div>
              )),
              ...s.notReadyAddresses.map((a, i) => (
                <div key={`nr-${i}`} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-status-warning" />
                  <span className="font-mono text-silver-bright">{a.ip}</span>
                  <span className="text-status-warning">not ready</span>
                  {a.nodeName && <span className="text-silver-muted">({a.nodeName})</span>}
                </div>
              )),
            ])}
          </div>
        </div>
      ) : (
        <div className="text-xs text-silver-muted">No endpoints</div>
      )}
    </div>
  );
}

export default function ServicesPanel({ siteId }: Props) {
  const [namespace, setNamespace] = useState<string>('');
  const { data: namespaces } = useClusterNamespaces(siteId);
  const { data: services, isLoading } = useClusterServices(siteId, namespace || undefined);

  if (isLoading && !services) {
    return (
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="p-8 space-y-3 animate-pulse">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-surface-elevated rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <select
          value={namespace}
          onChange={(e) => setNamespace(e.target.value)}
          className="bg-surface border border-surface-elevated rounded-lg px-3 py-1.5 text-xs text-silver-bright focus:border-gold focus:outline-none"
        >
          <option value="">All namespaces</option>
          {namespaces?.map((ns) => (
            <option key={ns} value={ns}>{ns}</option>
          ))}
        </select>
      </div>

      <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
        {!services || services.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <Network size={40} className="mx-auto text-silver-muted/30" />
            <p className="text-silver-muted">No services found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-elevated">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Namespace</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Type</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Cluster IP</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Ports</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Endpoints</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Age</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => (
                  <ServiceRow key={`${svc.namespace}/${svc.name}`} siteId={siteId} service={svc} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
